import { TaskQueue, TaskId } from "./taskQueueApi";
import api from "./api";

export type RequestChatResponse = {
  response_message: any;
  chat_id: string;
};

export type ChatStreamEventPayload = {
  messages: any[];
  event_data: Record<string, unknown>;
};

const CHAT_STREAMING_PLACEHOLDER = "__chat_streaming__";

export function supportsChatResponseStreaming(): boolean {
  return (
    typeof fetch === "function" &&
    typeof ReadableStream !== "undefined" &&
    typeof TextDecoder !== "undefined"
  );
}

export class ChatRequestTaskQue extends TaskQueue<RequestChatResponse> {
  mapChatIdToTaskId: Record<
    string,
    TaskId | typeof CHAT_STREAMING_PLACEHOLDER
  > = {};

  constructor(token: string, pollInterval?: number) {
    super(token, pollInterval, "Chat response request was cancelled.");
  }

  private getChatKey(chatId?: number): string {
    return chatId !== undefined ? `${chatId}` : "__default__";
  }

  chatIsGenerating(chatId: number | undefined): boolean {
    const key = this.getChatKey(chatId);
    return this.mapChatIdToTaskId[key] !== undefined;
  }

  private async requestResponseViaPoll(
    chatId: number
  ): Promise<RequestChatResponse | undefined> {
    const chatKey = this.getChatKey(chatId);
    const response = await api.post<{ task_id: TaskId }>(
      `/api/_internal/gpt/chat/request_response`,
      { chat_id: chatId, stream: false },
      {
        headers: { token: this.token },
      }
    );

    const { task_id: taskId } = response.data;
    this.mapChatIdToTaskId[chatKey] = taskId;
    this.addTaskId(taskId, "Chat Response");

    try {
      const rawResult = await super.waitUntilTaskIsDone(taskId);
      const taskResult = (rawResult as unknown) as { messages: any[] };

      const responseMessage = taskResult.messages?.[0] || null;

      return {
        response_message: responseMessage,
        chat_id: String(chatId),
      };
    } finally {
      delete this.mapChatIdToTaskId[chatKey];
      if (this.taskIdExists(taskId)) {
        this.removeTaskId(taskId);
      }
    }
  }

  private async requestResponseViaSse(
    chatId: number,
    options?: {
      onStreamChunk?: (payload: ChatStreamEventPayload) => void;
    }
  ): Promise<RequestChatResponse | undefined> {
    const chatKey = this.getChatKey(chatId);
    this.mapChatIdToTaskId[chatKey] = CHAT_STREAMING_PLACEHOLDER;

    try {
      const response = await fetch("/api/_internal/gpt/chat/request_response", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: this.token,
        },
        credentials: "same-origin",
        body: JSON.stringify({ chat_id: chatId, stream: true }),
      });

      if (!response.ok) {
        throw new Error(`Chat response stream failed (${response.status})`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Chat response stream has no body");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let lastMessages: any[] | null = null;

      let isReading = true;

      while (isReading) {
        const { done, value } = await reader.read();
        if (done) {
          isReading = false;
        }
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() ?? "";
        for (const block of blocks) {
          if (!block.trim()) {
            continue;
          }
          let eventType = "";
          let dataPayload = "";
          for (const line of block.split("\n")) {
            if (line.startsWith("event:")) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith("data:")) {
              dataPayload += line.slice(5).trimStart();
            }
          }
          if (eventType === "error") {
            let msg = "Chat stream error";
            try {
              const errObj = JSON.parse(dataPayload) as { message?: string };
              if (errObj.message) {
                msg = errObj.message;
              }
            } catch {
              // keep default
            }
            throw new Error(msg);
          }
          if (dataPayload) {
            const payload = JSON.parse(dataPayload) as ChatStreamEventPayload;
            lastMessages = payload.messages;
            options?.onStreamChunk?.(payload);
          }
        }
      }

      const responseMessage = lastMessages?.[0] ?? null;
      return {
        response_message: responseMessage,
        chat_id: String(chatId),
      };
    } finally {
      delete this.mapChatIdToTaskId[chatKey];
    }
  }

  async requestResponse(
    chatId: number,
    options?: {
      onStreamChunk?: (payload: ChatStreamEventPayload) => void;
    }
  ): Promise<RequestChatResponse | undefined> {
    if (this.chatIsGenerating(chatId)) {
      console.warn(
        "Chat is already generating a response, Please wait for it to finish"
      );
      return;
    }

    if (supportsChatResponseStreaming()) {
      return this.requestResponseViaSse(chatId, options);
    }

    return this.requestResponseViaPoll(chatId);
  }
}
